import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { API_URL } from "../../../config/api";
import Format from "../../../helpers/Format";
import { GenerateBilletsResponse } from "../../../services/AgilisServices";
import Button from "../../Button";
import Loader from "../../Loader";
import Modal from "../Modal";

import style from "./styles.module.scss";

interface Props {
  open: boolean;
  payment?: GenerateBilletsResponse;
  title?: string;
  onClose(): void;
}

interface PaymentDetails {
  barcode?: string;
  dueDate?: string;
  pixCode?: string;
  qrCode?: string;
  token?: string;
  type?: string;
  value?: number | string;
}

const keyI18n = "translations.consultDebts.receiptTypeItem.paymentModal";

function asRecord(value: unknown): Record<string, unknown> | undefined {
  return value && typeof value === "object"
    ? value as Record<string, unknown>
    : undefined;
}

function asString(value: unknown): string | undefined {
  return typeof value === "string" && value.trim() ? value : undefined;
}

function extractPaymentDetails(payment?: GenerateBilletsResponse): PaymentDetails {
  const response = asRecord(payment);
  const billet = asRecord(response?.billet) || response;
  const pix = asRecord(response?.pix) || asRecord(billet?.pix);

  return {
    barcode: asString(billet?.barcode),
    dueDate: asString(billet?.due_date),
    pixCode: asString(pix?.copy_past) || asString(response?.copy_past),
    qrCode: asString(pix?.qr_code) || asString(response?.qr_code),
    token: asString(billet?.token),
    type: asString(billet?.type),
    value: typeof billet?.value === "number" || typeof billet?.value === "string"
      ? billet.value
      : undefined,
  };
}

function getQrCodeSource(qrCode?: string): string | undefined {
  if (!qrCode) return undefined;

  const normalizedQrCode = qrCode.trim();

  if (/^(data:|https?:|blob:)/i.test(normalizedQrCode)) {
    return normalizedQrCode;
  }

  if (normalizedQrCode.startsWith("<svg")) {
    return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(normalizedQrCode)}`;
  }

  return `data:image/png;base64,${normalizedQrCode}`;
}

export default function PaymentModal({ open, payment, title, onClose }: Props) {
  const { t } = useTranslation();
  const feedbackTimeoutRef = useRef<number>();
  const [copied, setCopied] = useState(false);
  const details = useMemo(() => extractPaymentDetails(payment), [payment]);
  const qrCodeSource = useMemo(() => getQrCodeSource(details.qrCode), [details.qrCode]);
  const downloadUrl = details.token && details.type
    ? `${API_URL}/boletos/${details.token}/baixar?tipo=${details.type}&origin=portal`
    : undefined;

  const clearCopyFeedback = useCallback(() => {
    if (feedbackTimeoutRef.current) {
      window.clearTimeout(feedbackTimeoutRef.current);
      feedbackTimeoutRef.current = undefined;
    }

    setCopied(false);
  }, []);

  const copyPixCode = useCallback(async () => {
    if (!details.pixCode) return;

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(details.pixCode);
      } else {
        throw new Error("Clipboard API unavailable");
      }
    } catch (_error) {
      const textarea = document.createElement("textarea");
      textarea.value = details.pixCode;
      textarea.setAttribute("readonly", "");
      textarea.style.position = "absolute";
      textarea.style.left = "-9999px";
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
    }

    clearCopyFeedback();
    setCopied(true);
    feedbackTimeoutRef.current = window.setTimeout(() => {
      setCopied(false);
      feedbackTimeoutRef.current = undefined;
    }, 3000);
  }, [clearCopyFeedback, details.pixCode]);

  useEffect(() => () => clearCopyFeedback(), [clearCopyFeedback]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="large"
      dialogClassName={style.dialog}
      headerClassName={style.header}
      title={title || t(`${keyI18n}.title`)}
      closeLabel={t(`${keyI18n}.close`)}
      headerActions={downloadUrl && (
        <Button
          variant="filled"
          size="small"
          href={downloadUrl}
          target="_blank"
          icon={<span className="material-symbols-sharp">download</span>}
        >
          {t(`${keyI18n}.downloadBillet`)}
        </Button>
      )}
    >
      <div className={style.body}>
        <div className={style.summary}>
          <div className={style.summaryItem}>
            <span className="pgm-label-large">{t(`${keyI18n}.amount`)}</span>
            <span className={`pgm-body-medium ${style.summaryValue}`}>
              {Format.currency(details.value || 0)}
            </span>
          </div>
          <div className={style.summaryItem}>
            <span className="pgm-label-large">{t(`${keyI18n}.dueDate`)}</span>
            <span className={`pgm-body-medium ${style.summaryValue}`}>
              {details.dueDate ? Format.date(details.dueDate, "long") : "-"}
            </span>
          </div>
        </div>

        <div className={`pgm-body-small ${style.notice}`} role="status">
          <span className="material-symbols-sharp" aria-hidden="true">info</span>
          <span>{t(`${keyI18n}.notice`)}</span>
        </div>

        <div className={style.paymentInstructions}>
          <section className={style.steps}>
            <h3 className={`pgm-body-medium ${style.sectionTitle}`}>
              {t(`${keyI18n}.howToPay`)}
            </h3>
            <ol className={`pgm-body-medium ${style.stepsList}`}>
              {(t(`${keyI18n}.steps`, { returnObjects: true }) as unknown as string[]).map((step) => (
                <li key={step}>{step}</li>
              ))}
            </ol>
          </section>

          <section className={style.pix}>
            <p className={`pgm-body-medium ${style.pixDescription}`}>
              {t(`${keyI18n}.scanQrCode`)}
            </p>
            <div className={style.qrCode} aria-label={t(`${keyI18n}.qrCodeLabel`)}>
              {qrCodeSource ? (
                <img src={qrCodeSource} alt={t(`${keyI18n}.qrCodeLabel`)} />
              ) : (
                <Loader />
              )}
            </div>

            <div className={`pgm-body-medium ${style.or}`}>
              <span>{t(`${keyI18n}.manualPixCode`)}</span>
            </div>

            <div className={style.pixCode}>
              <span className="pgm-body-medium">{details.pixCode || t(`${keyI18n}.loadingPix`)}</span>
              <Button
                variant="text"
                iconButton
                size="extra-small"
                type="button"
                disabled={!details.pixCode}
                aria-label={t(`${keyI18n}.${copied ? "copied" : "copyPixCode"}`)}
                tooltip={t(`${keyI18n}.${copied ? "copied" : "copyPixCode"}`)}
                tooltipPlacement="bottom"
                onClick={copyPixCode}
                icon={(
                  <span className={`material-symbols-sharp ${style.copyIcon}`}>
                    {copied ? "check" : "content_copy"}
                  </span>
                )}
              />
            </div>
          </section>
        </div>
      </div>
    </Modal>
  );
}
